import type { AxiosInstance } from "axios";
import { TEMPLATE_EDITOR_NAMESPACE } from "../../config/studio-env";
import {
  capabilityForRequest,
  isCapabilityEnabled,
} from "../capabilities";
import { CapabilityUnavailableError } from "../errors";
import { isAbsoluteUrl } from "./is-absolute-url";

/**
 * Rewrite a call-site path onto the editor-token namespace.
 *
 * The moved editor code calls fetcher/mutator with short paths
 * ("/store-templates/:id/draft"); the instance's baseURL supplies /api/v2.
 * This injects the fixed segment between them — replacing the dashboard's
 * merchant|system lookup — and is idempotent because a 401-retried request
 * re-enters the request interceptors.
 */
export function toEditorPath(url: string): string {
  // Absolute URLs (Azure SAS uploads) are someone else's namespace entirely.
  // Includes protocol-relative "//host/path" — axios sends these cross-host too.
  if (isAbsoluteUrl(url)) return url;
  let path = url.startsWith("/") ? url : `/${url}`;
  // Defensive: strip prefixes a legacy caller might still pass.
  path = path.replace(/^\/api\/v2(?=\/)/, "");
  path = path.replace(/^\/merchant(?=\/)/, "");
  // Idempotence: "/template-editor/..." survives the "/merchant" strip above.
  // The bare-root equality check matters too — without it, a rewritten root
  // "/template-editor" (no trailing segment) falls through to the return
  // below and gets double-prefixed to "/merchant/template-editor/template-editor".
  if (path === "/template-editor" || path.startsWith("/template-editor/")) {
    return `/merchant${path}`;
  }
  return `${TEMPLATE_EDITOR_NAMESPACE}${path}`;
}

/**
 * The Studio's namespace + capability gate, as an installable function —
 * NOT an import side effect like the dashboard's interceptors, so nothing
 * touches the shared axios instance until Plan 2C swaps it deliberately.
 *
 * Deliberately absent, versus the dashboard's namespace.ts:
 * - no x-store header — the editor token is already store-scoped, and the
 *   Studio's URL has no /store/:id segment to derive one from;
 * - no tokenService/localStorage namespace lookup — the segment is fixed.
 */
export function installStudioNamespace(instance: AxiosInstance): number {
  return instance.interceptors.request.use((config) => {
    const url = config.url ?? "";

    // Absolute URLs (Azure SAS uploads) are someone else's namespace entirely.
    // Retried API requests always carry relative rewritten URLs, so no gating is lost.
    if (isAbsoluteUrl(url)) {
      return config;
    }

    // Gate BEFORE any rewrite, token fetch, or network: a request for an
    // unshipped endpoint group must cost nothing and fail with a typed,
    // UI-explainable error.
    const capability = capabilityForRequest(config.method ?? "get", url);
    if (capability && !isCapabilityEnabled(capability)) {
      return Promise.reject(new CapabilityUnavailableError(capability));
    }

    config.url = toEditorPath(url);
    if (!config.headers.has("Content-Type")) {
      config.headers.set("Content-Type", "application/json");
    }
    return config;
  });
}
